import { useState, useMemo } from 'react';
import { DateTime } from 'luxon';
import { ProjectOverviewDto, ProjectWithAssigneesDto, TimeLogUserSummaryWithUserInfoDto } from '../../../commonTypes/acs';
import { useHttpGetProjectSummaryOverview, useHttpGetUserTimeLogs, useHttpGetUserTimelog, useHttpSubmitTimeLogByOwner } from '../../../hooks';
import { useProjectTimesheetAttribution } from '../../../hooks/project/projectApi';
import { Table, TableRow, TableHeadCell, TableCell, MixerLoader, Pagination, Modal } from '../../Common';
import CalendarMonthShifter from '../../CalendarMonthShifter';
import MonthPicker from '../../MonthPicker';
import { getCurrencySymbol } from '../../../util/general';
import DeleteSubmissionInterval from '../../modalContent/UnsubmitInterval';
import { dispatch } from '../../../store';
import { invalidateTimesheetCache } from '../../../store/slices/projects.slice';
import { buildProjectTimesheetRowsFromRateAttribution, ProjectTimesheetTableRow } from '../../../util/timesheet/buildProjectTimesheetRows';
import { formatSecondsAsHhMmSs } from '../../../util/timesheet/formatSecondsAsHhMmSs';
import { generateUserOnProject } from '../../../util/TimeSheetHelpers';

interface ProjectTimesheetTabProps {
    project: ProjectWithAssigneesDto;
    showOnlyOwnData?: boolean;
    currentUserSub?: string;
    showRates?: boolean;
    canManageSubmissions?: boolean;
    currentUserName?: string;
    currentUserRole?: string;
    currentUserImageUrl?: string;
    currentUserRate?: number | null;
}

type TimesheetRow = ProjectTimesheetTableRow & {
    showActions?: boolean;
    givenName?: string;
    familyName?: string;
};

type SortColumn = 'MEMBER' | 'ROLE' | 'HOURLY_RATE' | 'UNSUBMITTED' | 'SUBMITTED';

const hasLoggedTime = (value: string) => value !== '00:00:00';

export const ProjectTimesheetTab = ({
    project,
    showOnlyOwnData = false,
    currentUserSub,
    showRates = false,
    canManageSubmissions = true,
    currentUserName,
    currentUserRole,
    currentUserImageUrl,
    currentUserRate,
}: ProjectTimesheetTabProps) => {
    const [currentMonth, setCurrentMonth] = useState<DateTime>(DateTime.now());
    const [sortBy, setSortBy] = useState<SortColumn>('MEMBER');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedSummary, setSelectedSummary] = useState<TimeLogUserSummaryWithUserInfoDto | null>(null);
    const [attributionRevision, setAttributionRevision] = useState(0);

    const month = currentMonth.toFormat('MMM');
    const year = currentMonth.year;
    const { exec: submitTimeLogByOwner } = useHttpSubmitTimeLogByOwner();
    const { exec: getUserTimelog } = useHttpGetUserTimelog();

    const periodStart = useMemo(() => currentMonth.startOf('month'), [currentMonth]);
    const periodEnd = useMemo(() => currentMonth.endOf('month'), [currentMonth]);

    const { isReady: isProjectSummaryReady, data } = useHttpGetProjectSummaryOverview(
        showOnlyOwnData ? '' : project.id,
        showOnlyOwnData ? '' : month,
        showOnlyOwnData ? 0 : year
    );

    const {
        totalSubmitted,
        totalUnsubmitted,
        rateBreakdown,
        isReady: isTimeLogsReady,
    } = useHttpGetUserTimeLogs(showOnlyOwnData ? { month, year, projectId: project.id } : { month: '', year: 0, projectId: '' });

    const attribution = useProjectTimesheetAttribution({
        projectId: project.id,
        periodStart,
        periodEnd,
        enabled: showRates,
        onlyUserSub: showOnlyOwnData ? currentUserSub : undefined,
        revision: attributionRevision,
    });

    const overviewByUserSub = useMemo(() => {
        const m = new Map<string, ProjectOverviewDto>();
        if (data) {
            for (const item of data) {
                m.set(item.userSub, item);
            }
        }
        return m;
    }, [data]);

    const memberOverviewRow = useMemo((): Map<string, ProjectOverviewDto> | null => {
        if (!showOnlyOwnData || !currentUserSub) return null;
        return new Map<string, ProjectOverviewDto>([
            [
                currentUserSub,
                {
                    userSub: currentUserSub,
                    email: '',
                    member: currentUserName || 'Unknown',
                    role: currentUserRole || '',
                    title: currentUserRole || '',
                    unsubmitted: Math.round(totalUnsubmitted * 3600),
                    submitted: Math.round(totalSubmitted * 3600),
                    imageUrl: currentUserImageUrl,
                },
            ],
        ]);
    }, [showOnlyOwnData, currentUserSub, currentUserName, currentUserRole, currentUserImageUrl, totalSubmitted, totalUnsubmitted]);

    const currentProjectAssignee = useMemo(
        () => project.projectAssignees?.find(assignee => assignee.userSub === currentUserSub),
        [project.projectAssignees, currentUserSub]
    );

    const timesheetData = useMemo<TimesheetRow[]>(() => {
        if (showRates && attribution.isReady) {
            const ovMap = memberOverviewRow ?? overviewByUserSub;
            const built = buildProjectTimesheetRowsFromRateAttribution(attribution.rateRows, project.id, attribution.buckets, ovMap, project, {
                onlyUserSub: showOnlyOwnData ? currentUserSub : undefined,
            });
            if (built.length > 0) {
                return built.map(r => {
                    const assignee = project.projectAssignees?.find(a => a.userSub === r.userSub);
                    return {
                        ...r,
                        givenName: assignee?.givenName,
                        familyName: assignee?.familyName,
                    };
                });
            }
        }

        if (showOnlyOwnData) {
            const fallbackNames = (currentUserName || '').trim().split(/\s+/).filter(Boolean);

            if (rateBreakdown?.length) {
                return rateBreakdown.map((br, i) => ({
                    rowKey: `${currentUserSub || 'me'}__${String(br.hourlyRate)}__${i}`,
                    userSub: currentUserSub || '',
                    member: currentUserName || 'Unknown',
                    givenName: currentProjectAssignee?.givenName || fallbackNames[0] || currentUserName || 'Unknown',
                    familyName: currentProjectAssignee?.familyName || fallbackNames.slice(1).join(' '),
                    role: currentUserRole || '',
                    hourlyRate: br.hourlyRate,
                    totalUnsubmitted: formatSecondsAsHhMmSs(Math.round(br.totalUnsubmitted * 3600)),
                    totalSubmitted: formatSecondsAsHhMmSs(Math.round(br.totalSubmitted * 3600)),
                    imageUrl: currentUserImageUrl,
                }));
            }
            return [
                {
                    rowKey: `${currentUserSub || 'me'}__aggregate`,
                    userSub: currentUserSub || '',
                    member: currentUserName || 'Unknown',
                    givenName: currentProjectAssignee?.givenName || fallbackNames[0] || currentUserName || 'Unknown',
                    familyName: currentProjectAssignee?.familyName || fallbackNames.slice(1).join(' '),
                    role: currentUserRole || '',
                    hourlyRate: currentUserRate ?? null,
                    totalUnsubmitted: formatSecondsAsHhMmSs(Math.round(totalUnsubmitted * 3600)),
                    totalSubmitted: formatSecondsAsHhMmSs(Math.round(totalSubmitted * 3600)),
                    imageUrl: currentUserImageUrl,
                },
            ];
        }

        if (!data) return [];

        return data.map(item => {
            const assignee = project.projectAssignees?.find(a => a.userSub === item.userSub);
            const rateFromApi = item.hourlyRate;
            const rate: number | null = rateFromApi !== undefined && rateFromApi !== null ? rateFromApi : assignee?.rate ?? null;
            const role = (item.title || item.role || assignee?.title || '').trim();

            return {
                rowKey: `${item.userSub}__${String(rate)}__${item.submitted}__${item.unsubmitted}`,
                userSub: item.userSub,
                member: item.member,
                givenName: assignee?.givenName,
                familyName: assignee?.familyName,
                role,
                hourlyRate: rate,
                totalUnsubmitted: formatSecondsAsHhMmSs(item.unsubmitted),
                totalSubmitted: formatSecondsAsHhMmSs(item.submitted),
                imageUrl: item.imageUrl,
            };
        });
    }, [
        showRates,
        attribution.isReady,
        attribution.rateRows,
        attribution.buckets,
        memberOverviewRow,
        overviewByUserSub,
        project,
        showOnlyOwnData,
        currentUserSub,
        totalSubmitted,
        totalUnsubmitted,
        rateBreakdown,
        currentUserName,
        currentUserRole,
        currentUserImageUrl,
        currentUserRate,
        data,
        currentProjectAssignee?.familyName,
        currentProjectAssignee?.givenName,
    ]);

    const sortedData = useMemo(() => {
        const result = [...timesheetData];

        result.sort((a, b) => {
            const dir = sortDirection === 'ASC' ? 1 : -1;

            switch (sortBy) {
                case 'MEMBER':
                    return dir * a.member.localeCompare(b.member);
                case 'ROLE':
                    return dir * a.role.localeCompare(b.role);
                case 'HOURLY_RATE':
                    return dir * ((a.hourlyRate ?? 0) - (b.hourlyRate ?? 0));
                case 'UNSUBMITTED':
                    return dir * a.totalUnsubmitted.localeCompare(b.totalUnsubmitted);
                case 'SUBMITTED':
                    return dir * a.totalSubmitted.localeCompare(b.totalSubmitted);
                default:
                    return 0;
            }
        });

        return result;
    }, [timesheetData, sortBy, sortDirection]);

    const sortedDataWithActions = useMemo(() => {
        const firstIdxByUser = new Map<string, number>();
        sortedData.forEach((row, idx) => {
            if (!firstIdxByUser.has(row.userSub)) firstIdxByUser.set(row.userSub, idx);
        });
        return sortedData.map((row, idx) => ({
            ...row,
            showActions: firstIdxByUser.get(row.userSub) === idx,
        }));
    }, [sortedData]);

    const paginatedData = useMemo(() => {
        const startIndex = currentPage * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedDataWithActions.slice(startIndex, endIndex);
    }, [sortedDataWithActions, currentPage, itemsPerPage]);

    const handleSort = (column: SortColumn) => {
        setSortDirection(prev => (column === sortBy ? (prev === 'ASC' ? 'DESC' : 'ASC') : 'ASC'));
        setSortBy(column);
        setCurrentPage(0);
    };

    const getArrowClass = (column: SortColumn) => {
        if (sortBy !== column) return 'invoices__header-cell--sortable__arrow';
        return `invoices__header-cell--sortable__arrow invoices__header-cell--sortable__arrow--${sortDirection.toLowerCase()}`;
    };

    const handlePreviousMonth = () => {
        setCurrentMonth(prev => prev.minus({ month: 1 }));
    };

    const handleNextMonth = () => {
        setCurrentMonth(prev => prev.plus({ month: 1 }));
    };

    const handleMonthPickerChange = (date: DateTime) => {
        setCurrentMonth(date.startOf('month'));
        setCurrentPage(0);
    };

    const refreshTimesheetData = () => {
        dispatch(invalidateTimesheetCache(project.id));
        setAttributionRevision(r => r + 1);
    };

    const handleSubmit = (row: TimesheetRow) => {
        submitTimeLogByOwner(
            {
                userSub: row.userSub,
                projectId: project.id,
                month,
                year,
            },
            refreshTimesheetData
        );
    };

    const handleUnsubmit = (row: TimesheetRow) => {
        setSelectedSummary({
            userSub: row.userSub,
            submitted: 0,
            unsubmitted: 0,
            email: '',
            givenName: row.member,
            imageUrl: row.imageUrl,
        });
    };

    const isReady = showOnlyOwnData
        ? isTimeLogsReady && (!showRates || attribution.isReady)
        : isProjectSummaryReady && (!showRates || attribution.isReady);

    const handleGeneratePdf = (row: TimesheetRow) => {
        const from = encodeURIComponent(currentMonth.startOf('month').toISO());
        const to = encodeURIComponent(currentMonth.endOf('month').toISO());

        getUserTimelog(
            { from, to, projectId: project.id, userSub: row.userSub },
            {
                onSuccess: res => {
                    void generateUserOnProject(res, project.clientName, row.givenName || row.member, row.familyName || '', project.name, {
                        startDate: currentMonth.startOf('month'),
                        endDate: currentMonth.endOf('month'),
                    });
                },
            }
        );
    };

    if (!isReady) {
        return <MixerLoader position="centerOnParent" />;
    }

    return (
        <div className="project-timesheet-tab">
            <div className="project-timesheet-tab__header">
                <h2 className="project-timesheet-tab__title">Total Logged Time</h2>
                <div className="project-timesheet-tab__month-controls">
                    <CalendarMonthShifter
                        formatOptions={{ month: 'long', year: 'numeric' }}
                        currentDate={currentMonth}
                        previous={handlePreviousMonth}
                        next={handleNextMonth}
                    />
                    <MonthPicker currentMonth={currentMonth} updateCalendarFromMonthPicker={handleMonthPickerChange} />
                </div>
            </div>
            <div className="projects-table__wrapper">
                <Table className="invoices__table">
                    <TableRow className="invoices__header-cell--background">
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                Member
                                <img
                                    src={sortBy === 'MEMBER' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('MEMBER')}
                                    onClick={() => handleSort('MEMBER')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                Role
                                <img
                                    src={sortBy === 'ROLE' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('ROLE')}
                                    onClick={() => handleSort('ROLE')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        {showRates && (
                            <TableHeadCell className="invoices__header-cell--background">
                                <div className="invoices__header-cell invoices__header-cell--sortable">
                                    Rate
                                    <img
                                        src={sortBy === 'HOURLY_RATE' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                        className={getArrowClass('HOURLY_RATE')}
                                        onClick={() => handleSort('HOURLY_RATE')}
                                        alt="Sort"
                                    />
                                </div>
                            </TableHeadCell>
                        )}
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                Total Unsubmitted
                                <img
                                    src={sortBy === 'UNSUBMITTED' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('UNSUBMITTED')}
                                    onClick={() => handleSort('UNSUBMITTED')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                Total Submitted
                                <img
                                    src={sortBy === 'SUBMITTED' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('SUBMITTED')}
                                    onClick={() => handleSort('SUBMITTED')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                    </TableRow>

                    {paginatedData.map((row, index) => (
                        <TableRow key={row.rowKey} className={`invoices__row ${index % 2 === 0 ? 'invoices__row--white' : 'invoices__row--gray'}`}>
                            <TableCell className="invoices__cell--background">
                                <div className="invoices__cell">
                                    <div className="project-timesheet-tab__member">
                                        <img
                                            className="project-timesheet-tab__avatar"
                                            src={row.imageUrl || '/svg/person.svg'}
                                            alt={row.member}
                                            onError={e => ((e.target as HTMLImageElement).src = '/svg/person.svg')}
                                        />
                                        <span className="invoices__cell-text">{row.member}</span>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell className="invoices__cell--background">
                                <div className="invoices__cell">
                                    <span className="invoices__cell-text">{row.role}</span>
                                </div>
                            </TableCell>
                            {showRates && (
                                <TableCell className="invoices__cell--background">
                                    <div className="invoices__cell">
                                        <span className="invoices__cell-text">
                                            {row.hourlyRate != null ? `${getCurrencySymbol(project.currency)}${row.hourlyRate.toFixed(2)}` : '-'}
                                        </span>
                                    </div>
                                </TableCell>
                            )}
                            <TableCell className="invoices__cell--background">
                                <div className="invoices__cell">
                                    <span className="invoices__cell-text">{row.totalUnsubmitted}</span>
                                </div>
                            </TableCell>
                            <TableCell className="invoices__cell--background">
                                <div className="invoices__cell">
                                    <span className="invoices__cell-text">{row.totalSubmitted}</span>
                                </div>
                            </TableCell>
                            {canManageSubmissions && (
                                <TableCell className="invoices__cell--background">
                                    <div className="invoices__cell">
                                        {row.showActions ? (
                                            <>
                                                {hasLoggedTime(row.totalSubmitted) ? (
                                                    <div className="project-timesheet-tab__actions">
                                                        <button
                                                            type="button"
                                                            className="project-timesheet-tab__action-button"
                                                            onClick={() => handleUnsubmit(row)}
                                                        >
                                                            Unsubmit
                                                        </button>
                                                        <div className="project-timesheet-tab__action-icon-wrapper">
                                                            <button
                                                                type="button"
                                                                className="project-timesheet-tab__icon-button"
                                                                onClick={() => handleGeneratePdf(row)}
                                                                title="Generate PDF"
                                                                aria-label="Generate PDF"
                                                            >
                                                                <img src="/svg/eye.svg" alt="Generate PDF" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : hasLoggedTime(row.totalUnsubmitted) ? (
                                                    <button
                                                        type="button"
                                                        className="project-timesheet-tab__action-button"
                                                        onClick={() => handleSubmit(row)}
                                                    >
                                                        Submit
                                                    </button>
                                                ) : null}
                                            </>
                                        ) : null}
                                    </div>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                    {sortedData.length > 10 && (
                        <TableRow className="projects-table__pagination-row">
                            <TableCell colSpan={(showRates ? 5 : 4) + (canManageSubmissions ? 1 : 0)} className="projects-table__pagination-cell">
                                <Pagination
                                    total={sortedData.length}
                                    elementsPerPage={itemsPerPage}
                                    setElementsPerPage={setItemsPerPage}
                                    page={currentPage}
                                    setPage={setCurrentPage}
                                />
                            </TableCell>
                        </TableRow>
                    )}
                </Table>
            </div>
            <Modal isVisible={selectedSummary !== null} closeModal={() => setSelectedSummary(null)}>
                {selectedSummary && (
                    <DeleteSubmissionInterval
                        summary={selectedSummary}
                        projectId={project.id}
                        startDate={currentMonth.startOf('month').toISODate() ?? ''}
                        endDate={currentMonth.endOf('month').toISODate() ?? ''}
                        closeModal={() => setSelectedSummary(null)}
                        onSuccessCallback={refreshTimesheetData}
                    />
                )}
            </Modal>
        </div>
    );
};
